import { Box, Button, Checkbox, DialogContent, DialogTitle, FormControlLabel, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { createApiResponse, withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../api/adminApi";
import type { Fundraising } from "../../../../../api/types/fundraisingTypes";
import FundraisingStatusUpdateIcon from "../FundraisingStatusUpdateIcon";
import type { FundraisingStatusTransition } from "../fundraisingsPageTypes";

interface Props {
  onClose: () => void;
  onUpdated: (fundraising: Fundraising) => void;
  fundraisingId: string;
  fundraisingEtag?: string;
  fundId?: string;
  areNotificationsSetUp?: boolean;
  statusTransition: FundraisingStatusTransition;
}

const updateFundraising = withErrorHandling(adminApi.updateFundraising);

const ChangeFundraisingStatusDialog = ({
  onClose,
  onUpdated,
  fundraisingId,
  fundraisingEtag,
  fundId,
  areNotificationsSetUp,
  statusTransition,
}: Props) => {
  const getFundraisingAccess = useCallback(
    () =>
      statusTransition.notificationsEnabled && fundId
        ? adminApi.getFundraisingAccess(fundId)
        : Promise.resolve(createApiResponse([])),
    [fundId, statusTransition.notificationsEnabled]
  );

  const [access, error, { isFetching }] = useFetch(getFundraisingAccess);

  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [saving, setSaving] = useState(false);
  const [notifyUsers, setNotifyUsers] = useState(false);

  const handleSubmit = async () => {
    if (!fundraisingEtag) {
      sendNotificationError(
        "Unable to update status: the fundraising version is missing. Please refresh and try again."
      );
      return;
    }

    setSaving(true);
    const [fundraising, error] = await updateFundraising(fundraisingId, {
      expectedEtag: fundraisingEtag,
      status: statusTransition.to,
      sendEmailNotifications: notifyUsers,
    });
    setSaving(false);

    if (error) {
      sendNotificationError(error.message);
    } else {
      sendNotification("Status updated successfully");
      onUpdated(fundraising);
    }
  };

  if (error) {
    logError(error, "[ChangeFundraisingStatusDialog] getFundraisingAccess");
  }

  const contactCount = access?.filter((a) => !!a.contactEmail).length ?? 0;

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>{statusTransition.label}</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        {isFetching ? (
          <InlineLoader />
        ) : (
          <Stack spacing={2} sx={{ alignItems: "center" }}>
            <FundraisingStatusUpdateIcon status={statusTransition.to} />
            <Stack spacing={1} sx={{ alignItems: "center" }}>
              <Typography noWrap variant="h6">
                {statusTransition.label}
              </Typography>
              {statusTransition.detailLines.map((line, index) => (
                // eslint-disable-next-line @eslint-react/no-array-index-key
                <Typography key={index} color="textSecondary" sx={{ textAlign: "center" }}>
                  {line}
                </Typography>
              ))}
            </Stack>
            {statusTransition.notificationsEnabled && contactCount > 0 && (
              <Box sx={{ mt: 3 }}>
                <FormControlLabel
                  disabled={!areNotificationsSetUp}
                  control={<Checkbox checked={notifyUsers} onChange={(e) => setNotifyUsers(e.target.checked)} />}
                  label={
                    <Typography color={areNotificationsSetUp ? "textPrimary" : "textDisabled"}>
                      Send notification emails to <strong>{`${contactCount} contacts`}</strong>
                    </Typography>
                  }
                />
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="contained" loading={saving} onClick={handleSubmit} disabled={isFetching}>
          {`${statusTransition.dialogActionLabel}${notifyUsers ? " & Notify" : ""}`}
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default ChangeFundraisingStatusDialog;
